/**
 * A report snapshot at its address (roadmap RP-7: "snapshot / share").
 *
 *   /prioritization/reports/snapshots/:id          in the app
 *   /prioritization/reports/snapshots/:id/print    the print view (PDF)
 *   /embed/reports/snapshots/:id                   no chrome, for an iframe
 *
 * It draws the numbers the report saved — never re-runs the report — so a
 * snapshot from a Monday stand-up still says what it said on Monday. Every
 * bar keeps the drill it had, so the item list behind a number is one click
 * away. Readers must be signed in to the workspace (RLS, migration 825).
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ExternalLink, Link2, Printer, Trash2 } from 'lucide-react';
import { EmptyState, Modal, Panel } from '../../../components/ds';
import { btnDanger, btnPrimary, btnSecondary } from '../../../components/mono/primitives';
import { ReportStatCard } from '../../../components/prioritization/reports/shared/ReportStatCard';
import { ReportBarList } from '../../../components/prioritization/reports/shared/ReportBarList';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import {
  deleteReportSnapshot,
  fetchReportSnapshot,
  snapshotPath,
  type ReportSnapshot,
} from '../../../lib/reportSnapshotsService';
import { ReportPrintShell } from './ReportPrintShell';
import { formatWhen } from '../../../lib/calendarDate';

export interface ReportSnapshotPageProps {
  /** No app chrome, no actions beyond opening the app. */
  embed?: boolean;
  /** Inside the print shell. */
  print?: boolean;
}

function scopeLine(s: ReportSnapshot): string {
  const parts = [s.config.rangeLabel];
  if (s.config.segmentLabel) parts.push(`by ${s.config.segmentLabel}`);
  return parts.join(' · ');
}

function SnapshotBody({ snapshot, print = false }: { snapshot: ReportSnapshot; print?: boolean }) {
  const { stats, series } = snapshot.summary;
  return (
    <div className="space-y-6">
      {snapshot.note && <p className="text-sm text-[var(--text-sec)]">{snapshot.note}</p>}
      {stats.length > 0 && (
        <div className={`grid gap-4 ${print ? 'grid-cols-3' : 'grid-cols-2 md:grid-cols-4'}`}>
          {stats.map((st) => (
            <ReportStatCard key={st.label} label={st.label} value={st.value} sub={st.sub} />
          ))}
        </div>
      )}
      {series.length > 0 && (
        <div className={`grid gap-4 ${print ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
          {series.map((sr) => (
            <Panel key={sr.title} title={sr.title} label={sr.description}>
              <ReportBarList rows={print ? sr.rows.map((r) => ({ ...r, href: undefined })) : sr.rows} />
            </Panel>
          ))}
        </div>
      )}
      {stats.length === 0 && series.length === 0 && (
        <EmptyState title="This snapshot holds no numbers" description="The report had nothing to show when it was taken." />
      )}
      {snapshot.config.statement && (
        <p className="font-mono text-xs text-[var(--text-ter)]">Query: {snapshot.config.statement}</p>
      )}
    </div>
  );
}

export function ReportSnapshotPage({ embed = false, print = false }: ReportSnapshotPageProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const addNotification = useUIStore((s) => s.addNotification);
  const [snapshot, setSnapshot] = useState<ReportSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    void fetchReportSnapshot(id).then(({ snapshot: s, error: e }) => {
      if (cancelled) return;
      setSnapshot(s);
      setError(e);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [id]);

  const liveHref = snapshot ? `${snapshot.config.reportPath}${snapshot.config.search ? `?${snapshot.config.search}` : ''}` : '/prioritization/reports';

  const copyLink = useCallback(async () => {
    if (!id) return;
    const url = `${window.location.origin}${snapshotPath(id)}`;
    try {
      await navigator.clipboard.writeText(url);
      addNotification({ type: 'success', title: 'Link copied', message: 'Anyone signed in to this workspace can open it.', duration: 3000 });
    } catch {
      addNotification({ type: 'info', title: 'Copy this link', message: url, duration: 8000 });
    }
  }, [id, addNotification]);

  const handleDelete = useCallback(async () => {
    if (!id) return;
    const { error: e } = await deleteReportSnapshot(id);
    setConfirmDelete(false);
    if (e) { addNotification({ type: 'error', title: 'Not removed', message: e, duration: 5000 }); return; }
    addNotification({ type: 'success', title: 'Snapshot removed', message: 'Its address no longer resolves.', duration: 3000 });
    navigate('/prioritization/reports');
  }, [id, addNotification, navigate]);

  if (loading) {
    return <p className="p-6 text-sm text-[var(--text-ter)]">Loading snapshot…</p>;
  }
  if (!snapshot) {
    return (
      <div className="p-6">
        <EmptyState
          size="page"
          title="No snapshot at this address"
          description={error ?? 'It may have been removed, or it belongs to a workspace you are not a member of.'}
          action={!embed ? <Link to="/prioritization/reports" {...btnSecondary()}>Reports</Link> : undefined}
        />
      </div>
    );
  }

  const meta = `${snapshot.config.reportName} · ${scopeLine(snapshot)} · taken ${formatWhen(snapshot.createdAt, { withYear: true })}${snapshot.createdByName ? ` by ${snapshot.createdByName}` : ''}`;

  if (print) {
    return (
      <ReportPrintShell title={snapshot.name} backHref={snapshotPath(snapshot.id)} meta={meta}>
        <SnapshotBody snapshot={snapshot} print />
      </ReportPrintShell>
    );
  }

  return (
    <div className={`space-y-5 ${embed ? 'mx-auto max-w-5xl p-6' : ''}`} data-report-snapshot={snapshot.id}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          {!embed && (
            <nav className="mb-1 flex items-center gap-1 font-mono text-[0.68rem] uppercase tracking-[0.12em] text-[var(--text-ter)]" aria-label="Breadcrumb">
              <Link to="/prioritization/reports" className="hover:text-[var(--text-sec)]">Reports</Link>
              <span aria-hidden="true">›</span>
              <Link to={liveHref} className="hover:text-[var(--text-sec)]">{snapshot.config.reportName}</Link>
              <span aria-hidden="true">›</span>
              <span className="text-[var(--text-sec)]">Snapshot</span>
            </nav>
          )}
          <h1 className="text-[1.75rem] leading-tight text-[var(--text-pri)]" style={{ fontFamily: 'var(--serif)', fontWeight: 400 }}>{snapshot.name}</h1>
          <p className="mt-1 text-sm text-[var(--text-sec)]">{meta}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {embed ? (
            <a href={`${window.location.origin}${snapshotPath(snapshot.id)}`} target="_blank" rel="noopener" {...btnSecondary()}>
              <ExternalLink className="h-3.5 w-3.5" /> Open in Priorities.ai
            </a>
          ) : (
            <>
              <button type="button" {...btnSecondary()} onClick={() => void copyLink()} title="Copy this snapshot's address">
                <Link2 className="h-3.5 w-3.5" /> Copy link
              </button>
              <Link to={`${snapshotPath(snapshot.id)}/print`} {...btnSecondary()} title="Print or save as PDF">
                <Printer className="h-3.5 w-3.5" /> Print
              </Link>
              <Link to={liveHref} {...btnPrimary()} title="The live report with the same controls">
                Open live report
              </Link>
              <button type="button" {...btnDanger()} onClick={() => setConfirmDelete(true)} title="Remove this snapshot">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </div>
      </div>

      <SnapshotBody snapshot={snapshot} />

      <Modal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Remove this snapshot?"
        description="Its address stops resolving for everyone. The live report is unaffected."
        size="sm"
        footer={
          <>
            <button type="button" {...btnSecondary()} onClick={() => setConfirmDelete(false)}>Keep it</button>
            <button type="button" {...btnDanger()} onClick={() => void handleDelete()}>Remove</button>
          </>
        }
      />
    </div>
  );
}

export default ReportSnapshotPage;
